import { useEffect, useMemo, useState } from "react";
import { TrendingUp, CheckCircle2, Clock, XCircle, Trophy, Hourglass } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, CartesianGrid,
} from "recharts";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatCard from "../components/StatCard";

function fmt(n) { return Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function shortDate(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso.slice(0, 10); } }

function StatusBadge({ status }) {
  const map = {
    paid: { cls: "p-pill-emerald", icon: CheckCircle2, label: "Paid" },
    pending: { cls: "p-pill-amber", icon: Clock, label: "Pending" },
    rejected: { cls: "p-pill-rose", icon: XCircle, label: "Rejected" },
  };
  const m = map[status] || { cls: "p-pill-slate", icon: Clock, label: status || "—" };
  const I = m.icon;
  return <span className={`p-pill ${m.cls}`}><I size={10} /> {m.label}</span>;
}

export default function Commissions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/aff-panel/commissions"); setRows(data.rows || []); }
      catch { toast.error("Failed to load commissions"); }
      finally { setLoading(false); }
    })();
  }, []);

  const totals = useMemo(() => ({
    paid: rows.filter(r => r.status === "paid").reduce((s, r) => s + Number(r.commission_amount || 0), 0),
    pending: rows.filter(r => r.status === "pending").reduce((s, r) => s + Number(r.commission_amount || 0), 0),
    rejected: rows.filter(r => r.status === "rejected").reduce((s, r) => s + Number(r.commission_amount || 0), 0),
    count: rows.length,
  }), [rows]);

  // Last 8 periods bar chart (oldest to newest)
  const series = useMemo(() => {
    return [...rows]
      .sort((a, b) => String(a.period_start || "").localeCompare(String(b.period_start || "")))
      .slice(-8)
      .map((r) => ({
        label: shortDate(r.period_end).slice(0, 6),
        commission: Number(r.commission_amount || 0),
        net_loss: Number(r.net_loss_amount || 0),
      }));
  }, [rows]);

  return (
    <div>
      <PageHeader title="Commission History" eyebrow="Earnings" subtitle="Every past commission period at a glance." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5">
        <StatCard label="Lifetime Paid" value={totals.paid} prefix="৳ " decimals={2} accent="gold" icon={Trophy} delay={0} />
        <StatCard label="Pending" value={totals.pending} prefix="৳ " decimals={2} accent="amber" icon={Hourglass} delay={60} />
        <StatCard label="Rejected" value={totals.rejected} prefix="৳ " decimals={2} accent="rose" icon={XCircle} delay={120} />
        <StatCard label="Total Periods" value={totals.count} accent="slate" icon={TrendingUp} delay={180} />
      </div>

      {series.length > 1 && (
        <div className="p-glass p-fade-up mb-5" style={{ padding: "20px 22px" }}>
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={14} className="text-emerald-300" />
            <div className="text-sm font-bold text-white">Commission Trend</div>
            <div className="text-[11px] text-white/45 ml-1">last {series.length} periods</div>
          </div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="barGold" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#a7f3d0" stopOpacity={1} />
                    <stop offset="100%" stopColor="#991b1b" stopOpacity={1} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="label" stroke="rgba(255,255,255,0.35)" tickLine={false} axisLine={false} fontSize={10} />
                <Tooltip
                  contentStyle={{
                    background: "rgba(10,8,16,0.95)",
                    border: "1px solid rgba(34, 192, 115, 0.4)",
                    borderRadius: 10,
                    color: "#f5f1e6",
                    fontSize: 12,
                  }}
                  labelStyle={{ color: "#a7f3d0", fontWeight: 700 }}
                  cursor={{ fill: "rgba(34, 192, 115, 0.06)" }}
                  formatter={(v) => `৳ ${fmt(v)}`}
                />
                <Bar dataKey="commission" fill="url(#barGold)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="p-glass p-fade-up p-fade-up-1" style={{ padding: "16px 18px" }}>
        {loading ? (
          <div className="py-10 text-center">
            <div className="p-skeleton mx-auto" style={{ width: 160, height: 14, marginBottom: 8 }} />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center">
            <div className="inline-flex w-16 h-16 rounded-full items-center justify-center mb-3" style={{
              background: "linear-gradient(135deg, rgba(34, 192, 115, 0.18), rgba(34, 192, 115, 0.02))",
              border: "1px solid rgba(34, 192, 115, 0.4)",
            }}>
              <Trophy size={26} className="text-emerald-300" />
            </div>
            <div className="text-white/65 text-sm font-semibold">No commission history yet</div>
            <div className="text-white/45 text-[12px] mt-1">Your first cycle’s commission will appear here.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="p-table" data-testid="commissions-table">
              <thead><tr>
                <th>Period</th>
                <th className="text-right">Net Loss</th>
                <th className="text-right">Commission</th>
                <th>Paid At</th>
                <th>Status</th>
              </tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>{shortDate(r.period_start)} – {shortDate(r.period_end)}</td>
                    <td className="text-right">৳ {fmt(r.net_loss_amount)}</td>
                    <td className="text-right"><span className="p-gold-text font-black">৳ {fmt(r.commission_amount)}</span></td>
                    <td className="text-white/70">{shortDate(r.paid_at)}</td>
                    <td><StatusBadge status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
